import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Calendar } from "./ui/calendar";
import { formatEUR } from "../lib/utils";
import { FormField, FormSubmissionStatus, ProgressBar } from "./FormComponents";
import { useResponsive } from "../hooks/useResponsive";
import { PrimaryButton } from "./ui/StandardButton";
import HeroHeading from "./ui/HeroHeading";
import { colors } from "../styles/colors";
import InteractiveCard from "./InteractiveCard";
import SeoAlternateLinks from "./seo/SeoAlternateLinks";
import { buildAbsoluteUrl, derivePageMeta } from "../utils/seo";
import { useToast } from "./ToastProvider";
import { CalendarDays } from "lucide-react";
import LazyVideo from "./LazyVideo";
import { trackMetaEvent } from "../hooks/useMetaPixel";
import { useContent } from "../cms/ContentContext";
import { sparHandelse, sparLead } from "../utils/spar";

const PACKAGE_DEFINITIONS = [
  { id: "1-day", translationKey: "taster", price: 380 },
  { id: "3-day", translationKey: "threeDay", price: 1250 },
  { id: "5-day", translationKey: "adventure", price: 1750, popular: true },
  { id: "7-day", translationKey: "complete", price: 2250 }
];

const COUNTRY_CODES = [
  { value: "+46", label: "Sweden (+46)" },
  { value: "+47", label: "Norway (+47)" },
  { value: "+45", label: "Denmark (+45)" },
  { value: "+358", label: "Finland (+358)" },
  { value: "+49", label: "Germany (+49)" },
  { value: "+48", label: "Poland (+48)" },
  { value: "+44", label: "United Kingdom (+44)" },
  { value: "+31", label: "Netherlands (+31)" },
  { value: "+33", label: "France (+33)" },
  { value: "+34", label: "Spain (+34)" },
  { value: "+39", label: "Italy (+39)" },
  { value: "+41", label: "Switzerland (+41)" },
  { value: "+43", label: "Austria (+43)" },
  { value: "+32", label: "Belgium (+32)" },
  { value: "+1", label: "USA / Canada (+1)" }
];

const priceById = PACKAGE_DEFINITIONS.reduce(
  (acc, pkg) => ({ ...acc, [pkg.id]: pkg.price }),
  {}
);

const normalizeDate = (date) => {
  if (!date) {
    return null;
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const MESSAGE_MAX_LENGTH = 1000;

const BookExperience = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();

  const meta = derivePageMeta({
    t,
    i18n,
    pageKey: "book",
    routeKey: "book",
    defaults: {
      ogImage: "/og/book.jpg"
    }
  });

  const ogImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.ogImage);
  const twitterImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.twitterImage);

  const heroVideo = getMedia('booking.hero.videoSrc', '/optimized_videos/coldexperience1-opt.mp4');
  const heroPoster = getMedia('booking.hero.poster', '/images/Nya_bilder/IMG_6698.webp');
  const heroSpacing = isMobile
    ? "px-4 pt-24 pb-16"
    : "px-4 sm:px-6 lg:px-8 pt-32 pb-24";
  const heroMaxWidth = isMobile ? "max-w-sm" : "max-w-5xl";

  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedDate, setSelectedDate] = useState();
  const [numAdults, setNumAdults] = useState("");
  const [numChildren, setNumChildren] = useState("");
  const [childrenAges, setChildrenAges] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+46");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [botField, setBotField] = useState("");
  const apiBaseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const formularPaborjat = useRef(false);

  // Ett påbörjat formulär räknas en gång per session, inte per tangenttryck.
  const markeraPaborjad = () => {
    if (formularPaborjat.current) return;
    formularPaborjat.current = true;
    sparHandelse("form_start", { formular: "bokning" });
  };

  const packages = useMemo(
    () =>
      PACKAGE_DEFINITIONS.map((pkg) => ({
        ...pkg,
        name: t(`packages.${pkg.translationKey}.name`, pkg.translationKey),
        duration: t(`packages.${pkg.translationKey}.duration`, ""),
        description: t(`packages.${pkg.translationKey}.description`, ""),
        highlights: (() => {
          const items = t(`packages.${pkg.translationKey}.highlights`, {
            returnObjects: true
          });
          return Array.isArray(items) ? items : [];
        })()
      })),
    [t]
  );

  const locale = i18n?.language || "en";
  const formattedSelectedDate = selectedDate
    ? selectedDate.toLocaleDateString(locale)
    : t("booking.dateNotSelected", "Not selected");

  const normalizedToday = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  })();

  const validateForm = () => {
    const newErrors = {};
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim().replace(/\+/g, "");
    const trimmedMessage = message.trim();
    const normalizedPhone = `${phoneCountryCode} ${trimmedPhone}`.trim();
    const phoneDigitCount = normalizedPhone.replace(/\D/g, "").length;

    if (!selectedPackage) {
      newErrors.package = t("booking.packageError");
    }

    if (!selectedDate) {
      newErrors.date = t("booking.dateErrorRequired");
    } else {
      const normalizedSelectedDate = normalizeDate(selectedDate);
      if (normalizedSelectedDate && normalizedSelectedDate < normalizedToday) {
        newErrors.date = t("booking.dateErrorPast");
      }
    }

    if (!trimmedName) {
      newErrors.name = t("booking.nameErrorRequired");
    } else if (trimmedName.length < 2) {
      newErrors.name = t("booking.nameErrorShort");
    }

    if (!trimmedEmail) {
      newErrors.email = t("booking.emailErrorRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = t("booking.emailErrorInvalid");
    }

    if (!trimmedPhone) {
      newErrors.phone = t("booking.phoneErrorRequired");
    } else if (!/^[0-9\s-]+$/.test(trimmedPhone) || phoneDigitCount < 7) {
      newErrors.phone = t("booking.phoneErrorInvalid");
    }

    if (trimmedMessage.length > MESSAGE_MAX_LENGTH) {
      newErrors.message = t("booking.messageErrorTooLong", {
        max: MESSAGE_MAX_LENGTH
      });
    }

    const parsedAdults = parseInt(numAdults, 10);
    if (!numAdults || isNaN(parsedAdults) || parsedAdults < 1) {
      newErrors.numAdults = t("booking.numAdultsError");
    }

    const parsedChildren = parseInt(numChildren, 10);
    if (numChildren !== "" && !isNaN(parsedChildren) && parsedChildren < 0) {
      newErrors.numChildren = t("booking.numChildrenError");
    }

    const childCount = parseInt(numChildren, 10) || 0;
    if (childCount > 0) {
      if (childrenAges.length !== childCount) {
        newErrors.childrenAges = t("booking.childrenAgesErrorRequired");
      } else if (
        childrenAges.some((age) => {
          if (age === null || age === undefined || age === "") {
            return true;
          }
          const value = Number(age);
          return Number.isNaN(value) || value < 0 || value > 17;
        })
      ) {
        newErrors.childrenAges = t("booking.childrenAgesErrorRange");
      }
    }

    if (!consent) {
      newErrors.consent = t("booking.consentError");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNumChildrenChange = (value) => {
    // Allow empty string for editing, otherwise sanitize to 0+
    if (value === "") {
      setNumChildren("");
      setChildrenAges([]);
      return;
    }
    const sanitized = Math.max(0, parseInt(value, 10) || 0);
    setNumChildren(value);
    setChildrenAges((prev) => {
      const next = prev.slice(0, sanitized);
      while (next.length < sanitized) {
        next.push("");
      }
      return next;
    });
  };

  const handleChildrenAgeChange = (value, index) => {
    setChildrenAges((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error({
        title: t("booking.toastValidationTitle"),
        description: t("booking.toastValidationDesc")
      });
      return;
    }

    setSubmitted(false);
    setIsSubmitting(true);

    try {
      if (botField) {
        setIsSubmitting(false);
        return;
      }

      const trimmedName = name.trim();
      const trimmedEmail = email.trim();
      const trimmedPhone = phone.trim().replace(/\+/g, "");
      const trimmedMessage = message.trim();
      const normalizedPhone = `${phoneCountryCode} ${trimmedPhone}`.trim();
      const parsedChildrenAges = childrenAges
        .map((age) => parseInt(age, 10))
        .filter((age) => !Number.isNaN(age));
      const vuxna = parseInt(numAdults, 10) || 1;
      const barn = parseInt(numChildren, 10) || 0;

      // Gasten gar till SCC forst och oberoende av sajtens egen backend. Gar
      // Render ner ska leadet anda ligga i Gustavs CRM. Samtycket ar validerat
      // hogre upp, darfor far vi skicka det.
      sparLead({
        name: trimmedName,
        email: trimmedEmail,
        phone: normalizedPhone,
        message: trimmedMessage,
        language: locale,
        group_size: vuxna + barn,
        intent: "yes",
        consent_given: consent === true,
        form: {
          package: selectedPackage || null,
          preferred_date: selectedDate ? selectedDate.toISOString().split("T")[0] : null,
          adults: vuxna,
          children: barn,
          children_ages: parsedChildrenAges,
          message: trimmedMessage || null,
          language: locale
        }
      });

      if (!apiBaseUrl) {
        throw new Error("Backend URL saknas");
      }

      const backendPayload = {
        client_name: trimmedName,
        email: trimmedEmail,
        phone: normalizedPhone,
        package: selectedPackage,
        date: selectedDate ? selectedDate.toISOString().split("T")[0] : "",
        numAdults: vuxna,
        numChildren,
        childrenAges: parsedChildrenAges,
        message: trimmedMessage,
        consent: consent ? "yes" : "no",
        locale
      };

      const backendResponse = await globalThis.fetch(
        `${apiBaseUrl}/api/status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(backendPayload)
        }
      );

      if (!backendResponse.ok) {
        const details = await backendResponse.text();
        throw new Error(details || "Backend submission failed");
      }

      setSubmitted(true);
      sparHandelse("form_submit", { formular: "bokning", upplevelse: selectedPackage || null });

      // Track Lead event in Meta Pixel
      trackMetaEvent("Lead", {
        content_name: selectedPackage,
        content_category: "Booking",
        value: priceById[selectedPackage] || 0,
        currency: "EUR"
      });

      setSelectedPackage("");
      setSelectedDate(undefined);
      setNumAdults("");
      setNumChildren("");
      setChildrenAges([]);
      setName("");
      setEmail("");
      setPhoneCountryCode("+46");
      setPhone("");
      setMessage("");
      setConsent(false);
      setErrors({});
      setBotField("");
    } catch (error) {
      console.error("Booking submission failed", error);
      toast.error({
        title: t("booking.toastErrorTitle"),
        description: t("booking.toastErrorDesc")
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedDateHelper =
    selectedDate && formattedSelectedDate
      ? t("booking.selectedDateText", { date: formattedSelectedDate })
      : null;

  const progressCurrentStep =
    submitted && !isSubmitting
      ? 3
      : selectedPackage && selectedDate
        ? 2
        : 1;

  const locationLine1 = t("contact.info.location.line1", "Swedish Lapland");
  const locationLine2 =
    locale === "sv"
      ? t("contact.info.location.line2", "Råstrand, Sverige")
      : locale === "de"
        ? t("contact.info.location.line2", "Råstrand, Schweden")
        : t("contact.info.location.line2", "Råstrand, Sweden");
  const primaryPhone = t("contact.info.phone.number", "+46 73 181 45 68");
  const secondaryPhone = "+46 70 222 97 65";
  const phoneNote = t("contact.info.phone.note", "Available 9am-6pm CET");
  const emailAddress = t("contact.info.email.address", "info@coldexperience.se");
  const emailNote = t("contact.info.email.note", "We respond within 24 hours");
  const mapsLabel = t("contact.openMaps", "Open in Maps");
  const findUsTitle = t("contact.findusTitle", "Find us");

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        {meta.description && (
          <meta name="description" content={meta.description} />
        )}
        {meta.keywords && <meta name="keywords" content={meta.keywords} />}
        <link rel="canonical" href={meta.canonicalUrl} />
        <meta property="og:title" content={meta.ogTitle} />
        <meta property="og:description" content={meta.ogDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={meta.canonicalUrl} />
        <meta name="twitter:image" content={twitterImageUrl} />
        {meta.jsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(meta.jsonLd)}
          </script>
        )}
      </Helmet>

      <SeoAlternateLinks
        routeKey="book"
        canonicalPath={meta.canonicalPath}
        siteUrl={meta.siteUrl}
      />

      <section
        data-hero="true"
        data-cms-section="booking:hero"
        data-cms-page="booking"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <LazyVideo
            src={heroVideo}
            poster={heroPoster}
            autoPlay
            muted
            loop
            playsInline
            preload={isMobile ? "none" : "metadata"}
            className="absolute inset-0"
            videoClassName="absolute inset-0 w-full h-full object-cover"
            pauseWhenOutOfView
          />
          <div
            className="absolute inset-0"
            style={{ background: colors.gradients.hero }}
          />
        </div>
        <div className={`relative z-10 w-full ${heroSpacing}`}>
          <div className={`${heroMaxWidth} mx-auto text-center`}>
            <HeroHeading
              primary={t("book.hero.title1")}
              accent={t("book.hero.title2")}
              className={`${isMobile ? "text-4xl" : "text-5xl md:text-6xl"
                } mb-6`}
            />
            <p
              className={`${isMobile ? "text-lg max-w-md" : "text-xl max-w-3xl"
                } mx-auto`}
              style={{ color: colors.text.secondary }}
            >
              {t("book.hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      <section
        data-cms-section="booking"
        data-cms-page="booking"
        className="px-4 sm:px-6 lg:px-8 py-16"
        style={{ background: colors.gradients.heroSolid }}
      >
        <div className="mx-auto max-w-6xl grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          {/* Booking form */}
          <InteractiveCard
            variant="glass"
            className="p-6 sm:p-8 space-y-6"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${colors.accent.primary}20` }}
                >
                  <CalendarDays
                    className="h-6 w-6"
                    style={{ color: colors.accent.primary }}
                  />
                </div>
                <h2
                  className="text-2xl font-semibold"
                  style={{ color: colors.text.primary }}
                >
                  {t("booking.formTitle")}
                </h2>
              </div>
              <div className="min-w-[120px] w-full sm:w-48">
                <ProgressBar currentStep={progressCurrentStep} totalSteps={3} />
              </div>
            </div>

            {isSubmitting && (
              <FormSubmissionStatus
                status="loading"
                message={t("booking.toastProcessingDesc")}
              />
            )}
            {submitted && !isSubmitting && (
              <FormSubmissionStatus
                status="success"
                message={t("booking.toastSuccessDesc")}
              />
            )}

            <form
              className="space-y-6"
              onSubmit={handleSubmit}
              // Fångar första gången kunden rör formuläret, oavsett vilket
              // fält. Bättre än att haka på varje enskild onChange.
              onFocusCapture={markeraPaborjad}
              name="booking"
            >
              <div className="hidden">
                <label>
                  Don’t fill this out if you're human:
                  <input
                    name="bot-field"
                    value={botField}
                    onChange={(event) => setBotField(event.target.value)}
                    autoComplete="off"
                  />
                </label>
              </div>
              <fieldset disabled={isSubmitting} className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    label={
                      <span style={{ color: colors.text.primary }}>
                        {t("form.numAdultsLabel")}
                      </span>
                    }
                    required
                    error={errors.numAdults}
                    helpText={
                      <span style={{ color: colors.text.secondary }}>
                        {t("form.numAdultsHelp")}
                      </span>
                    }
                  >
                    <input
                      type="number"
                      min={1}
                      className="w-full rounded-lg border-2 px-4 py-3 transition-all duration-200"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.95)",
                        borderColor: errors.numAdults
                          ? "#F87171"
                          : numAdults !== "" && parseInt(numAdults, 10) > 0
                            ? colors.accent.primary
                            : "rgba(255,255,255,0.3)",
                        color: colors.primary,
                        "--tw-ring-color": colors.accent.primary + "20"
                      }}
                      value={numAdults}
                      onChange={(event) => {
                        const inputValue = event.target.value;
                        // Allow empty string or valid positive numbers
                        if (inputValue === "" || /^\d+$/.test(inputValue)) {
                          setNumAdults(inputValue);
                        }
                        setErrors((prev) => ({ ...prev, numAdults: null }));
                        setSubmitted(false);
                      }}
                      placeholder={t("form.numAdultsPlaceholder")}
                    />
                  </FormField>

                  <FormField
                    label={
                      <span style={{ color: colors.text.primary }}>
                        {t("form.numChildrenLabel")}
                      </span>
                    }
                    error={errors.numChildren}
                    helpText={
                      <span style={{ color: colors.text.secondary }}>
                        {t("form.numChildrenHelp")}
                      </span>
                    }
                  >
                    <input
                      type="number"
                      min={0}
                      className="w-full rounded-lg border-2 px-4 py-3 transition-all duration-200"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.95)",
                        borderColor: errors.numChildren
                          ? "#F87171"
                          : numChildren !== "" && parseInt(numChildren, 10) > 0
                            ? colors.accent.primary
                            : "rgba(255,255,255,0.3)",
                        color: colors.primary,
                        "--tw-ring-color": colors.accent.primary + "20"
                      }}
                      value={numChildren}
                      onChange={(event) => {
                        const inputValue = event.target.value;
                        // Allow empty string or valid non-negative numbers
                        if (inputValue === "" || /^\d+$/.test(inputValue)) {
                          handleNumChildrenChange(inputValue);
                        }
                        setErrors((prev) => ({
                          ...prev,
                          numChildren: null,
                          childrenAges: null
                        }));
                        setSubmitted(false);
                      }}
                      placeholder={t("form.numChildrenPlaceholder")}
                    />
                  </FormField>
                </div>

                {parseInt(numChildren, 10) > 0 && (
                  <div className="space-y-2">
                    <span style={{ color: colors.text.primary }}>
                      {t("booking.childrenAgesLabel")}
                    </span>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {Array.from({ length: parseInt(numChildren, 10) || 0 }).map((_, index) => (
                        <input
                          key={`age-${index}`}
                          type="number"
                          min={0}
                          max={17}
                          className="w-full rounded-lg border-2 px-4 py-2 transition-all duration-200"
                          style={{
                            backgroundColor: "rgba(255,255,255,0.95)",
                            borderColor: errors.childrenAges
                              ? "#F87171"
                              : childrenAges[index]
                                ? colors.accent.primary
                                : "rgba(255,255,255,0.3)",
                            color: colors.primary,
                            "--tw-ring-color": colors.accent.primary + "20"
                          }}
                          value={childrenAges[index] || ""}
                          onChange={(event) => {
                            handleChildrenAgeChange(event.target.value, index);
                            setErrors((prev) => ({
                              ...prev,
                              childrenAges: null
                            }));
                            setSubmitted(false);
                          }}
                          placeholder={t("booking.childrenAgesPlaceholder", {
                            n: index + 1
                          })}
                        />
                      ))}
                    </div>
                    {errors.childrenAges && (
                      <div className="text-sm" style={{ color: "#F87171" }}>
                        {errors.childrenAges}
                      </div>
                    )}
                  </div>
                )}

                <FormField
                  label={
                    <span style={{ color: colors.text.primary }}>
                      {t("form.packageLabel")}
                    </span>
                  }
                  required
                  error={errors.package}
                  helpText={
                    <span style={{ color: colors.text.secondary }}>
                      {t("form.packageHelp")}
                    </span>
                  }
                >
                  <select
                    className="w-full rounded-lg border-2 px-4 py-3 transition-all duration-200"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.95)",
                      borderColor: errors.package
                        ? "#F87171"
                        : selectedPackage
                          ? colors.accent.primary
                          : "rgba(255,255,255,0.3)",
                      color: colors.primary,
                      "--tw-ring-color": colors.accent.primary + "20"
                    }}
                    value={selectedPackage}
                    onChange={(event) => {
                      setSelectedPackage(event.target.value);
                      setErrors((prev) => ({ ...prev, package: null }));
                      setSubmitted(false);
                    }}
                  >
                    <option value="">{t("form.packagePlaceholder")}</option>
                    {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.name} ({formatEUR(priceById[pkg.id])})
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField
                  label={
                    <span style={{ color: colors.text.primary }}>
                      {t("form.dateLabel")}
                    </span>
                  }
                  required
                  error={errors.date}
                  helpText={
                    <span style={{ color: colors.text.secondary }}>
                      {t("form.dateHelp")}
                    </span>
                  }
                >
                  <div className="space-y-2">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setErrors((prev) => ({ ...prev, date: null }));
                        setSubmitted(false);
                      }}
                      className="rounded-md border border-white/30 bg-white"
                    />
                    {selectedDateHelper && (
                      <p
                        className="text-sm"
                        style={{ color: colors.text.secondary }}
                      >
                        {selectedDateHelper}
                      </p>
                    )}
                  </div>
                </FormField>

                <FormField
                  label={
                    <span style={{ color: colors.text.primary }}>
                      {t("form.nameLabel")}
                    </span>
                  }
                  required
                  error={errors.name}
                >
                  <input
                    type="text"
                    className="w-full rounded-lg border-2 px-4 py-3 transition-all duration-200"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.95)",
                      borderColor: errors.name
                        ? "#F87171"
                        : name
                          ? colors.accent.primary
                          : "rgba(255,255,255,0.3)",
                      color: colors.primary,
                      "--tw-ring-color": colors.accent.primary + "20"
                    }}
                    value={name}
                    onChange={(event) => {
                      setName(event.target.value);
                      setErrors((prev) => ({ ...prev, name: null }));
                      setSubmitted(false);
                    }}
                    placeholder={t("form.namePlaceholder")}
                  />
                </FormField>

                <FormField
                  label={
                    <span style={{ color: colors.text.primary }}>
                      {t("form.emailLabel")}
                    </span>
                  }
                  required
                  error={errors.email}
                >
                  <input
                    type="email"
                    className="w-full rounded-lg border-2 px-4 py-3 transition-all duration-200"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.95)",
                      borderColor: errors.email
                        ? "#F87171"
                        : email
                          ? colors.accent.primary
                          : "rgba(255,255,255,0.3)",
                      color: colors.primary,
                      "--tw-ring-color": colors.accent.primary + "20"
                    }}
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setErrors((prev) => ({ ...prev, email: null }));
                      setSubmitted(false);
                    }}
                    placeholder={t("form.emailPlaceholder")}
                  />
                </FormField>

                <FormField
                  label={
                    <span style={{ color: colors.text.primary }}>
                      {t("form.phoneLabel")}
                    </span>
                  }
                  required
                  error={errors.phone}
                  helpText={
                    <span style={{ color: colors.text.secondary }}>
                      {t("form.phoneHelp")}
                    </span>
                  }
                >
                  <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)]">
                    <select
                      aria-label={t("form.phoneCountryCodeLabel", "Country code")}
                      className="w-full rounded-lg border-2 px-4 py-3 transition-all duration-200"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.95)",
                        borderColor: errors.phone
                          ? "#F87171"
                          : phone
                            ? colors.accent.primary
                            : "rgba(255,255,255,0.3)",
                        color: colors.primary,
                        "--tw-ring-color": colors.accent.primary + "20"
                      }}
                      value={phoneCountryCode}
                      onChange={(event) => {
                        setPhoneCountryCode(event.target.value);
                        setErrors((prev) => ({ ...prev, phone: null }));
                        setSubmitted(false);
                      }}
                    >
                      {COUNTRY_CODES.map((country) => (
                        <option key={country.value} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      className="w-full rounded-lg border-2 px-4 py-3 transition-all duration-200"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.95)",
                        borderColor: errors.phone
                          ? "#F87171"
                          : phone
                            ? colors.accent.primary
                            : "rgba(255,255,255,0.3)",
                        color: colors.primary,
                        "--tw-ring-color": colors.accent.primary + "20"
                      }}
                      value={phone}
                      onChange={(event) => {
                        setPhone(event.target.value.replace(/\+/g, ""));
                        setErrors((prev) => ({ ...prev, phone: null }));
                        setSubmitted(false);
                      }}
                      placeholder={t("form.phonePlaceholder")}
                    />
                  </div>
                </FormField>

                <FormField
                  label={
                    <span style={{ color: colors.text.primary }}>
                      {t("form.messageLabel")}
                    </span>
                  }
                  error={errors.message}
                  helpText={
                    <span style={{ color: colors.text.secondary }}>
                      {t("form.messageHelp")}
                    </span>
                  }
                >
                  <textarea
                    className="w-full rounded-lg border-2 px-4 py-3 transition-all duration-200"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.95)",
                      borderColor: errors.message
                        ? "#F87171"
                        : message
                          ? colors.accent.primary
                          : "rgba(255,255,255,0.3)",
                      color: colors.primary,
                      "--tw-ring-color": colors.accent.primary + "20"
                    }}
                    rows={4}
                    maxLength={MESSAGE_MAX_LENGTH}
                    value={message}
                    onChange={(event) => {
                      setMessage(event.target.value);
                      setErrors((prev) => ({ ...prev, message: null }));
                      setSubmitted(false);
                    }}
                    placeholder={t("form.messagePlaceholder")}
                  />
                </FormField>

                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded border border-white/20"
                      style={{ accentColor: colors.accent.primary }}
                      checked={consent}
                      onChange={(event) => {
                        setConsent(event.target.checked);
                        setErrors((prev) => ({ ...prev, consent: null }));
                        setSubmitted(false);
                      }}
                    />
                    <span
                      className="text-sm"
                      style={{ color: colors.text.primary }}
                    >
                      {t("booking.consentLabel")}
                    </span>
                  </label>
                  {errors.consent && (
                    <div className="text-sm" style={{ color: "#F87171" }}>
                      {errors.consent}
                    </div>
                  )}
                </div>

                <PrimaryButton
                  loading={isSubmitting}
                  type="submit"
                  className="w-full justify-center"
                  size="large"
                  disabled={isSubmitting}
                >
                  {t("booking.submitButton")}
                  {!isSubmitting && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="ml-2 h-5 w-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 12h14M12 5l7 7-7 7"
                      />
                    </svg>
                  )}
                </PrimaryButton>
              </fieldset>
            </form>
          </InteractiveCard>

          {/* Contact column */}
          <div className="space-y-6">
            <InteractiveCard variant="glass" className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${colors.accent.primary}20` }}
                >
                  <span
                    style={{
                      color: colors.accent.primary,
                      fontSize: "1.5rem",
                      lineHeight: 1
                    }}
                  >
                    📍
                  </span>
                </div>
                <h3
                  className="text-lg font-semibold"
                  style={{ color: colors.text.primary }}
                >
                  {findUsTitle}
                </h3>
              </div>
              <p className="text-sm" style={{ color: colors.text.secondary }}>
                {locationLine1}
                <br />
                {locationLine2}
              </p>
              <a
                href="https://maps.google.com/?q=65.2459661,17.9967012"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                style={{ color: colors.accent.primary }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-4 w-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                  />
                </svg>
                {mapsLabel}
              </a>
              <div className="space-y-1 text-sm">
                <a
                  href={`tel:${primaryPhone.replace(/\s+/g, "")}`}
                  className="flex items-center gap-2 font-medium hover:underline"
                  style={{ color: colors.accent.primary }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M22 16.92V19a2 2 0 01-2.18 2A19.72 19.72 0 013 5.18 2 2 0 015 3h2.09a2 2 0 012 1.72c.13 1.13.37 2.24.72 3.32a2 2 0 01-.45 2.11l-1.27 1.27a16.06 16.06 0 006.58 6.58l1.27-1.27a2 2 0 012.11-.45c1.08.35 2.19.59 3.32.72A2 2 0 0121 16.91z"
                    />
                  </svg>
                  {primaryPhone}
                </a>
                <a
                  href="tel:+46702229765"
                  className="flex items-center gap-2 font-medium hover:underline"
                  style={{ color: colors.accent.primary }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M22 16.92V19a2 2 0 01-2.18 2A19.72 19.72 0 013 5.18 2 2 0 015 3h2.09a2 2 0 012 1.72c.13 1.13.37 2.24.72 3.32a2 2 0 01-.45 2.11l-1.27 1.27a16.06 16.06 0 006.58 6.58l1.27-1.27a2 2 0 012.11-.45c1.08.35 2.19.59 3.32.72A2 2 0 0121 16.91z"
                    />
                  </svg>
                  {secondaryPhone}
                </a>
              </div>
            </InteractiveCard>

            <InteractiveCard variant="glass" className="p-6 space-y-2">
              <h3
                className="text-lg font-semibold"
                style={{ color: colors.text.primary }}
              >
                {t("contact.info.location.title", "Location")}
              </h3>
              <p className="text-sm" style={{ color: colors.text.secondary }}>
                {locationLine1}
                <br />
                {locationLine2}
              </p>
            </InteractiveCard>

            <InteractiveCard variant="glass" className="p-6 space-y-2">
              <h3
                className="text-lg font-semibold"
                style={{ color: colors.text.primary }}
              >
                {t("contact.info.phone.title", "Phone")}
              </h3>
              <div className="space-y-1">
                <a
                  href={`tel:${primaryPhone.replace(/\s+/g, "")}`}
                  className="font-medium text-sm hover:underline"
                  style={{ color: colors.text.primary }}
                >
                  {primaryPhone}
                </a>
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  {phoneNote}
                </p>
              </div>
            </InteractiveCard>

            <InteractiveCard variant="glass" className="p-6 space-y-2">
              <h3
                className="text-lg font-semibold"
                style={{ color: colors.text.primary }}
              >
                {t("contact.info.email.title", "Email")}
              </h3>
              <div className="space-y-1">
                <a
                  href={`mailto:${emailAddress}`}
                  className="font-medium text-sm hover:underline"
                  style={{ color: colors.text.primary }}
                >
                  {emailAddress}
                </a>
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  {emailNote}
                </p>
              </div>
            </InteractiveCard>

          </div>
        </div>
      </section>
    </>
  );
};

export default BookExperience;
